import AuthenticationService from '../services/AuthenticationService'
import UserService from '../services/UserService'
import User from '../models/User'
import { action, flow, makeObservable, observable, toJS } from 'mobx'
import Store from './Store'

class AuthenticationStore extends Store {
  @observable loggedUser = new User()
  @observable user;
  @observable roles = {}
  @observable initializing = true
  @observable isLoggedIn = false
  @observable startingPath = window.location.pathname

  constructor () {
    super()
    makeObservable(this)
  }

  @flow
  * initialize () {
    const hasValidSession = yield this.verifySession()
    if (hasValidSession) {
      yield this.fetchUserSession()
    }
    this.isLoggedIn = !!hasValidSession
    this.initializing = false
  }

  @flow
  * fetchUserSession () {
    const authUser = AuthenticationService.getUser()
    if (authUser) {
      this.roles = AuthenticationService.getRoles() || {}
      this.loggedUser = new User(authUser)
    }
    const data = yield UserService.profile()
    const { user, roles } = data
    this.roles = roles
    this.loggedUser = new User()
    this.loggedUser.setOriginalData(user)
    AuthenticationService.setUser(this.loggedUser.toJson())
    AuthenticationService.setRoles(toJS(this.roles))
  }

  @flow
  * clientLogin (uuid) {
    this.isLoading = true
    const response = yield AuthenticationService.clientLogin(uuid)
    if (response.success) {
      AuthenticationService.setUser(response.user)
      // TODO: Check for roles too
      this.isLoggedIn = true
      this.initializing = true
      yield this.fetchUserSession()
    } else {
      this.errorMessage = response.message
    }

    this.initializing = false
    this.isLoading = false
    return response
  }

  @flow
  * login (code, password) {
    this.isLoading = true
    const response = yield AuthenticationService.login(code, password)
    if (response.success) {
      AuthenticationService.setUser(response.user)
      // TODO: Check for roles too
      this.isLoggedIn = true
      this.initializing = true
      yield this.fetchUserSession()
    } else {
      this.errorMessage = response.message
    }

    this.initializing = false
    this.isLoading = false
    return response
  }

  @action
  cloneProfile () {
    this.user = new User()
    this.user.setOriginalData(toJS(this.loggedUser))
  }

  @action
  clearProfile () {
    this.user = null
  }

  @flow
  * saveProfile () {
    if (!this.user) {
      return
    }
    this.isSaving = true
    const response = yield UserService.saveProfile({
      first_name: this.user.first_name,
      last_name: this.user.last_name,
      email: this.user.email,
      password: this.user.password
    })
    if (response.success) {
      yield this.fetchUserSession()
      this.cloneProfile()
      this.addToast(response.message, 'success')
    } else {
      this.errorMessage = response.message
    }
    this.isSaving = false
  }

  /**
   * Disconnect user and remove token storage
   * @function logout
   * @returns {Generator<Promise<void>, void, *>}
   */
  @flow
  * logout () {
    yield AuthenticationService.logout()
    this.isLoggedIn = false
    this.loggedUser = new User()
    this.loggedUser.setOriginalData({})
  }

  @flow
  * verifySession () {
    try {
      const response = yield AuthenticationService.verifySession()
      if (response) {
        if (response.success) {
          return true
        }
      }
    } catch (e) {

    }

    return false
  }

  @action
  getRolesForController (controller) {
    return this.roles['*'] || this.roles[controller] || null
  }

  /**
   *
   * @param {string} controller
   * @param {string|array} actions
   * @returns {*|null}
   */
  @action
  getRoleForControllerAction (controller, actions) {
    const roles = this.getRolesForController(controller)

    if (roles === null) {
      return false
    }
    if (Array.isArray(actions)) {
      return roles.find(role => actions.find(action => action === role))
    }
    return roles.find(role => role === actions)
  }

  @action
  isAdmin () {
    return !!this.roles['*']
  }
}

export default AuthenticationStore
